// Who's the host, and passing it on. The server is the source of truth (join payload + server-sent 'host' messages).
// If the host just leaves, the signed-in person who has been here longest takes over after a short wait.
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import { claimMeetingHost, releaseMeetingHost, setMeetingHost } from '@/api/morningMeeting';

export const CLAIM_DELAY_MS = 5000;
const CLAIM_MAX_TRIES = 24;
// Signed-in people carry their app user id; guests' Daily user_id is just their session id.
export const isMember = (p) => !!p?.user_id && p.user_id !== p.session_id;
const joinedTs = (p) => new Date(p?.joined_at || 0).getTime() || Number.MAX_SAFE_INTEGER;
const errMsg = (e, f) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : f);

export function useHostHandoff({ dc, sessionId, enabled }) {
  const myId = dc.local?.user_id;
  const isHost = !!enabled && !!dc.hostId && myId === dc.hostId;
  const members = useMemo(() => Object.values(dc.participants).filter((p) => p && !p.local && isMember(p)), [dc.participants]);

  const shouldClaim = useMemo(() => {
    if (!enabled || !sessionId || dc.phase !== 'joined' || !isMember(dc.local)) return false;
    const all = Object.values(dc.participants).filter(Boolean);
    if (dc.hostId && all.some((p) => p.user_id === dc.hostId)) return false;
    const first = all.filter(isMember).sort((a, b) => joinedTs(a) - joinedTs(b) || String(a.session_id).localeCompare(String(b.session_id)))[0];
    return !!first?.local;
  }, [enabled, sessionId, dc.phase, dc.local, dc.participants, dc.hostId]);

  const setHostId = dc.setHostId;
  // Daily can keep listing a host who just left for ~30 s, so a refused claim is retried for a while.
  useEffect(() => {
    if (!shouldClaim) return undefined;
    let t;
    let tries = 0;
    const attempt = () => {
      tries += 1;
      claimMeetingHost(sessionId).then((r) => { if (r?.host_uid) setHostId(r.host_uid); })
        .catch(() => { if (tries < CLAIM_MAX_TRIES) t = setTimeout(attempt, CLAIM_DELAY_MS); });
    };
    t = setTimeout(attempt, CLAIM_DELAY_MS);
    return () => clearTimeout(t);
  }, [shouldClaim, sessionId, setHostId]);

  const me = useRef(myId);
  me.current = myId;
  const { onApp } = dc;
  useEffect(() => onApp((ev) => {
    if (ev?.fromId !== 'API' || ev?.data?.mm !== 'host') return;
    if (ev.data.uid === me.current) toast.success("You're the host now.");
    else toast(`${ev.data.name || 'Someone'} is now the host.`);
  }), [onApp]);

  const transfer = useCallback(async (userId) => {
    try {
      const r = await setMeetingHost(sessionId, userId);
      if (r?.host_uid) setHostId(r.host_uid);
      return true;
    } catch (e) {
      toast.error(errMsg(e, "Couldn't pass the host role on."));
      return false;
    }
  }, [sessionId, setHostId]);

  // Leaving without picking anyone: step down first so the next person takes over at once.
  const { leave } = dc;
  const leaveAsHost = useCallback(() => {
    if (isHost && sessionId) releaseMeetingHost(sessionId).catch(() => {});
    leave();
  }, [isHost, sessionId, leave]);

  return { isHost, hostId: dc.hostId, members, transfer, leaveAsHost };
}
